<template>
  <div class="p-0">
    <header class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-5">
      <div>
        <p class="breadcrumb">Licencias y permisos / Administracion / Articulos</p>
        <h1 class="text-2xl font-bold text-gray-900">Articulos de licencia</h1>
        <p class="mt-1 text-sm text-gray-600">Gestion centralizada de normativas y parametros documentados.</p>
      </div>
      <router-link to="/leave/admin/articles/new" class="primary-button">
        <i class="fa-solid fa-plus mr-2"></i>
        Nuevo articulo
      </router-link>
    </header>

    <nav class="module-tabs">
      <router-link to="/leave/inbox" class="module-tab">Bandeja de revision</router-link>
      <router-link to="/leave/calendar" class="module-tab">Calendario</router-link>
      <router-link to="/leave/admin/articles" class="module-tab module-tab-active">Articulos</router-link>
      <router-link to="/leave/admin/legal-documents" class="module-tab">Documentos legales</router-link>
    </nav>

    <section class="filter-card">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
        <label class="inline-flex items-center gap-2 text-sm font-semibold text-gray-700 md:mt-7">
          <input v-model="filters.active" type="checkbox" class="rounded border-gray-300 text-indigo-700" @change="fetchArticles" />
          Solo activos
        </label>
        <div>
          <label class="label">Categoria</label>
          <select v-model="filters.category" class="field" @change="fetchArticles">
            <option v-for="category in categories" :key="category.value" :value="category.value">
              {{ category.label }}
            </option>
          </select>
        </div>
        <div class="flex items-end">
          <button type="button" class="secondary-button w-full justify-center" @click="clearFilters">
            Limpiar filtros
          </button>
        </div>
      </div>
    </section>

    <div v-if="loading" class="py-10">
      <Loading />
    </div>

    <div v-else-if="error" class="alert-error">
      {{ error }}
      <button type="button" class="ml-2 underline font-semibold" @click="fetchArticles">Reintentar</button>
    </div>

    <div v-else-if="articles.length === 0" class="empty-card">
      <div class="mx-auto w-14 h-14 rounded-full bg-indigo-50 text-indigo-700 flex items-center justify-center mb-4">
        <i class="fa-solid fa-scale-balanced text-2xl"></i>
      </div>
      <h2 class="text-lg font-semibold text-gray-900">No hay articulos para mostrar</h2>
      <p class="mt-1 text-sm text-gray-500">Cambia los filtros o carga un articulo nuevo.</p>
    </div>

    <section v-else class="grid grid-cols-1 xl:grid-cols-2 gap-4">
      <article v-for="article in articles" :key="articleId(article)" class="article-card" :class="article.active === false ? 'border-l-gray-400' : 'border-l-emerald-500'">
        <div class="flex items-start justify-between gap-4">
          <div class="min-w-0">
            <div class="flex flex-wrap items-center gap-2">
              <span class="code-pill">{{ article.code }}</span>
              <h2 class="text-lg font-bold text-gray-900">{{ article.name }}</h2>
            </div>
            <p class="mt-2 text-sm text-gray-600">
              {{ categoryLabel(article.category) }}
              <span v-if="article.article_number"> · Art. {{ article.article_number }}</span>
            </p>
          </div>
          <span class="chip shrink-0" :class="article.active === false ? 'bg-gray-100 text-gray-600 border-gray-200' : 'bg-green-50 text-green-700 border-green-100'">
            {{ article.active === false ? 'Inactivo' : 'Activo' }}
          </span>
        </div>

        <div class="mt-4 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-gray-200 bg-gray-50 rounded-lg border border-gray-100">
          <div class="metric">
            <span>Pagado</span>
            <strong>{{ yesNo(article.paid) }}</strong>
          </div>
          <div class="metric">
            <span>Documentacion</span>
            <strong>{{ yesNo(article.requires_documentation) }}</strong>
          </div>
          <div class="metric">
            <span>Superposicion</span>
            <strong>{{ yesNo(article.can_overlap) }}</strong>
          </div>
        </div>

        <div class="mt-5 flex flex-wrap justify-between gap-2">
          <div class="flex flex-wrap gap-2">
            <router-link :to="`/leave/admin/articles/${articleId(article)}`" class="small-link">Ver</router-link>
            <router-link :to="`/leave/admin/articles/${articleId(article)}/edit`" class="small-link">Editar</router-link>
          </div>
          <button v-if="article.active !== false" type="button" class="danger-link" @click="openDeactivate(article)">
            Desactivar
          </button>
        </div>
      </article>
    </section>

    <div class="mt-6 flex items-center justify-between text-sm text-gray-500">
      <span>Mostrando {{ articles.length }} articulos</span>
      <router-link to="/leave/admin/legal-documents" class="inline-flex items-center font-semibold text-gray-700 hover:text-indigo-800">
        <i class="fa-regular fa-file-lines mr-2"></i>
        Documentos legales
      </router-link>
    </div>

    <div v-if="modal.article" class="modal-backdrop">
      <div class="modal-card">
        <div class="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4">
          <h2 class="text-lg font-bold text-gray-900">Desactivar articulo</h2>
          <button type="button" class="text-gray-400 hover:text-gray-700" @click="closeDeactivate">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div class="p-5">
          <div class="warning-box">
            <i class="fa-solid fa-triangle-exclamation text-red-600 mt-1"></i>
            <div>
              <p class="font-semibold text-gray-900">Desactivar {{ modal.article.code }} - {{ modal.article.name }}?</p>
              <p class="mt-2 text-sm text-gray-600">No aparecera en nuevas solicitudes. Las solicitudes existentes no se modifican.</p>
            </div>
          </div>
        </div>
        <div class="modal-actions">
          <button type="button" class="secondary-button" @click="closeDeactivate">Cancelar</button>
          <button type="button" :disabled="saving" class="danger-button disabled:opacity-50" @click="confirmDeactivate">
            {{ saving ? 'Desactivando...' : 'Desactivar' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from '@/components/commons/Loading.vue';
import { leaveArticlesApi } from '@/service/leave.service';
import { LEAVE_CATEGORIES, categoryLabel, unwrapList } from '@/utils/leave';

export default {
  name: 'LeaveAdminArticlesView',
  components: { Loading },
  data() {
    return {
      filters: {
        active: true,
        category: '',
      },
      articles: [],
      loading: false,
      saving: false,
      error: '',
      modal: {
        article: null,
      },
    };
  },
  computed: {
    categories() {
      return LEAVE_CATEGORIES;
    },
  },
  mounted() {
    this.fetchArticles();
  },
  methods: {
    categoryLabel,
    async fetchArticles() {
      this.loading = true;
      this.error = '';
      try {
        const params = {
          ...(this.filters.active ? { active: true } : {}),
          ...(this.filters.category ? { category: this.filters.category } : {}),
        };
        const response = await leaveArticlesApi.list(params);
        this.articles = unwrapList(response);
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudieron cargar los articulos.';
      } finally {
        this.loading = false;
      }
    },
    clearFilters() {
      this.filters = { active: true, category: '' };
      this.fetchArticles();
    },
    articleId(article) {
      return article.id || article.article_id || article.leave_article_id;
    },
    yesNo(value) {
      return value ? 'Si' : 'No';
    },
    openDeactivate(article) {
      this.modal.article = article;
    },
    closeDeactivate() {
      this.modal.article = null;
    },
    async confirmDeactivate() {
      if (!this.modal.article) return;
      this.saving = true;
      try {
        await leaveArticlesApi.deactivate(this.articleId(this.modal.article));
        this.closeDeactivate();
        await this.fetchArticles();
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudo desactivar el articulo.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.breadcrumb {
  @apply text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2;
}
.filter-card {
  @apply bg-white border border-gray-200 rounded-lg shadow-sm p-4 mb-5;
}
.field {
  @apply w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600;
}
.label {
  @apply block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1;
}
.article-card {
  @apply bg-white border border-l-4 border-gray-200 rounded-xl shadow-sm p-4 hover:border-indigo-200 transition-colors;
}
.metric {
  @apply px-3 py-2 text-sm;
}
.metric span {
  @apply block text-xs uppercase tracking-wide text-gray-500;
}
.metric strong {
  @apply block mt-1 text-gray-900;
}
.empty-card {
  @apply bg-white border border-dashed border-gray-300 rounded-xl p-10 text-center;
}
.primary-button {
  @apply inline-flex items-center justify-center px-4 py-2 text-sm font-semibold bg-indigo-700 text-white rounded-md hover:bg-indigo-800 shadow-sm;
}
.secondary-button {
  @apply inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50;
}
.danger-button {
  @apply inline-flex items-center justify-center px-4 py-2 text-sm font-semibold bg-indigo-900 text-white rounded-md hover:bg-indigo-800;
}
.small-link {
  @apply text-sm font-semibold text-indigo-700 hover:text-indigo-900;
}
.danger-link {
  @apply text-sm font-semibold text-red-700 hover:text-red-900;
}
.module-tabs {
  @apply bg-white border border-gray-200 rounded-lg shadow-sm p-1 mb-5 inline-flex flex-wrap gap-1;
}
.module-tab {
  @apply px-4 py-2 text-sm font-semibold text-gray-600 rounded-md hover:bg-gray-50;
}
.module-tab-active {
  @apply bg-indigo-900 text-white hover:bg-indigo-900;
}
.chip {
  @apply inline-flex px-2 py-1 text-xs font-semibold rounded-full border;
}
.code-pill {
  @apply inline-flex px-2 py-1 rounded bg-gray-100 text-xs font-mono text-gray-600;
}
.alert-error {
  @apply bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md;
}
.modal-backdrop {
  @apply fixed inset-0 z-40 bg-gray-900 bg-opacity-60 flex items-center justify-center p-4;
}
.modal-card {
  @apply bg-white rounded-lg shadow-xl w-full max-w-lg overflow-hidden;
}
.modal-actions {
  @apply bg-gray-50 px-5 py-4 flex justify-end gap-3;
}
.warning-box {
  @apply bg-red-50 border-l-4 border-red-500 p-4 flex gap-3;
}

/*
 * @apply bakes fixed light-mode colors into these scoped classes, so the
 * global dark-mode retrofit in style.css (which only overrides classes
 * literally present in the DOM) can never reach them. Same story for a few
 * inline badge/icon colors below that the retrofit doesn't cover for anyone.
 */
.dark .breadcrumb,
.dark .label,
.dark .metric span {
  @apply text-gray-400;
}
.dark .filter-card,
.dark .module-tabs {
  @apply bg-gray-800 border-gray-700;
}
.dark .field {
  @apply bg-gray-700 border-gray-600 text-gray-100;
}
.dark .article-card {
  @apply bg-gray-800 border-gray-700;
}
.dark .article-card:hover {
  border-color: rgba(99, 102, 241, 0.5);
}
.dark .metric strong {
  @apply text-gray-100;
}
.dark .empty-card {
  @apply bg-gray-800 border-gray-600;
}
.dark .secondary-button {
  @apply bg-gray-800 border-gray-600 text-gray-200;
}
.dark .secondary-button:hover {
  @apply bg-gray-700;
}
.dark .small-link:hover {
  color: theme('colors.indigo.200');
}
.dark .danger-link:hover {
  color: theme('colors.red.400');
}
.dark .module-tab {
  @apply text-gray-300;
}
.dark .module-tab:hover {
  @apply bg-gray-700;
}
.dark .code-pill {
  @apply bg-gray-700 text-gray-300;
}
.dark .alert-error {
  background-color: rgba(239, 68, 68, 0.15);
  border-color: rgba(239, 68, 68, 0.35);
  color: theme('colors.red.300');
}
.dark .modal-card {
  @apply bg-gray-800;
}
.dark .modal-actions {
  @apply bg-gray-900;
}
.dark .warning-box {
  background-color: rgba(239, 68, 68, 0.15);
}

.dark .border-green-100 {
  border-color: rgba(34, 197, 94, 0.35);
}
.dark .text-red-600 {
  color: theme('colors.red.400');
}
.dark .hover\:text-indigo-800:hover {
  color: theme('colors.indigo.300');
}
</style>
